import crypto from 'crypto-browserify'
import dayjs from 'dayjs'

export const getStringValueOrDefault = (value?: string, defaultValue?: string): string =>
    (value ?? '')?.trim() !== '' ? value! : defaultValue ? defaultValue : 'N/A'

export const getDateFromString = (dateString: string) => {
    const year = +dateString.substring(0, 4)
    const month = +dateString.substring(4, 6)
    const day = +dateString.substring(6, 8)
    return new Date(year, month - 1, day)
}

export const formatDateRange = (dateRange: string) => {
    const dates = dateRange?.split('-')
    if (dates.length > 1) {
        return `${formatDate(getDateFromString(dates[0]).getTime(), 'MM/DD/YYYY')} - ${formatDate(
            getDateFromString(dates[1]).getTime(),
            'MM/DD/YYYY',
        )}`
    }
    return formatDate(getDateFromString(dates[0]).getTime(), 'MM/DD/YYYY')
}

export const formatDate = (timestamp: number, format: string) => dayjs(timestamp).format(format)

export const fromRelationshipCode = (code: string) => {
    const codeMap = new Map<string, string>([
        ['1', 'Spouse'],
        ['4', 'Grandfather or Grandmother'],
        ['5', 'Grandson or Granddaughter'],
        ['7', 'Nephew or Niece'],
        ['10', 'Foster Child'],
        ['15', 'Ward of the court'],
        ['17', 'Step son or Step daughter'],
        ['18', 'Self'],
        ['19', 'Child'],
        ['20', 'Employee'],
        ['21', 'Unknown'],
        ['22', 'Handicapped/Dependent'],
        ['23', 'Sponsored Dependent'],
        ['24', 'Dependent of Minor Dependent'],
        ['29', 'Significant Other'],
        ['32', 'Mother'],
        ['33', 'Father'],
        ['36', 'Emancipated Minor'],
        ['39', 'Organ Donor'],
        ['40', 'Cadaver Donor'],
        ['41', 'Injured Plaintiff'],
        ['43', 'Child Where Insured Has No Financial Responsibility'],
        ['53', 'Life Partner'],
        ['G8', 'Other Relationship'],
    ])
    return codeMap.get(code)
}

export const fromPOSCode = (code: string) => {
    const codeMap = new Map<string, string>([
        ['01', 'Pharmacy'],
        ['02', 'Telehealth Provided Other than in Patient’s Home'],
        ['03', 'School'],
        ['04', 'Homeless Shelter'],
        ['05', 'Indian Health Service Free-standing Facility'],
        ['06', 'Indian Health Service Provider-based Facility'],
        ['07', 'Tribal 638 Free-standing Facility'],
        ['08', 'Tribal 638 Provider-based Facility'],
        ['09', 'Prison/Correctional Facility'],
        ['10', 'Telehealth Provided in Patient’s Home'],
        ['11', 'Office'],
        ['12', 'Handicapped'],
        ['13', 'Assisted Living Facility'],
        ['14', 'Group Home *'],
        ['15', 'Mobile Unit'],
        ['16', 'Temporary Lodging'],
        ['17', 'Walk-in Retail Health Clinic'],
        ['18', 'Place of Employment-Worksite'],
        ['19', 'Off Campus-Outpatient Hospital'],
        ['20', 'Urgent Care Facility'],
        ['21', 'Inpatient Hospital'],
        ['22', 'On Campus-Outpatient Hospital'],
        ['23', 'Emergency Room – Hospital'],
        ['24', 'Ambulatory Surgical Center'],
        ['25', 'Birthing Center'],
        ['26', 'Military Treatment Facility'],
        ['31', 'Skilled Nursing Facility'],
        ['32', 'Nursing Facility'],
        ['33', 'Ambulatory Surgical Center'],
        ['34', 'Hospice'],
        ['41', 'Ambulance - Land'],
        ['42', 'Ambulance – Air or Water'],
        ['49', 'Independent Clinic'],
        ['50', 'Federally Qualified Health Center'],
        ['51', 'Inpatient Psychiatric Facility'],
        ['52', 'Psychiatric Facility-Partial Hospitalization'],
        ['53', 'Community Mental Health Center'],
        ['54', 'Intermediate Care Facility/ Individuals with Intellectual Disabilities'],
        ['55', 'Residential Substance Abuse Treatment Facility'],
        ['56', 'Psychiatric Residential Treatment Center'],
        ['57', 'Non-residential Substance Abuse Treatment Facility'],
        ['58', 'Non-residential Opioid Treatment Facility'],
        ['60', 'Mass Immunization Center'],
        ['61', 'Comprehensive Inpatient Rehabilitation Facility'],
        ['62', 'Comprehensive Outpatient Rehabilitation Facility'],
        ['65', 'End-Stage Renal Disease Treatment Facility'],
        ['71', 'Public Health Clinic'],
        ['72', 'Rural Health Clinic'],
        ['81', 'Independent Laboratory'],
        ['99', 'Other Place of Service'],
    ])
    return codeMap.get(code)
}

export const fromFrequencyCode = (code: string) => {
    const codeMap = new Map<string, string>([
        ['1', 'New Claim'],
        ['2', 'Interim - First Claim'],
        ['3', 'Interim-Continuing Claims'],
        ['4', 'Interim - Last Claim (Not valid for PPS Bills)'],
        ['5', 'Late Charge Only'],
        ['7', 'Replacement of Prior Claim'],
        ['8', 'Void/Cancel of Prior Claim'],
        ['9', 'Final claim for a Home Health PPS Period'],
        ['A', 'Admission/Election Notice for Hospice'],
        ['B', 'Hospice Termination/ Revocation Notice'],
        ['C', 'Hospice Change of Provider Notice'],
        ['D', 'Hospice Election Void/Cancel'],
        ['E', 'Hospice Change of Ownership'],
        ['F', 'Beneficiary Initiated Adjustment Claim'],
        ['G', 'CWF Initiated Adjustment Claim'],
        ['H', 'CMS Initiated Adjustment Claim'],
        ['I', 'FI Adjustment Claim'],
        ['J', 'Initiated Adjustment Claim/Other'],
        ['K', 'OIG Initiated Adjustment Claim'],
        ['M', 'MSP Initiated Adjustment Claim'],
        ['O', 'Nonpayment/Zero Claims'],
        ['P', 'QIO Adjustment Claim'],
        ['Q', 'Reopening/Adjustment'],
        ['X', 'Void/Cancel a Prior Abbreviated Encounter Submission'],
        ['Y', 'Replacement of Prior Abbreviated Encounter Submission'],
        ['Z', 'New Abbreviated Encounter Submission'],
    ])
    return codeMap.get(code)
}

export const fromFacilityCode = (code: string) => {
    const codeMap = new Map<string, string>([
        ['1', 'Hospital'],
        ['2', 'Skilled Nursing Facility (SNF)'],
        ['3', 'Home Health'],
        ['4', 'Religious Nonmedical (Hospital)'],
        ['5', 'Religious Nonmedical (Extended Care)'],
        ['6', 'Intermediate Care'],
        ['7', 'Clinic or Hospital based End Stage Renal Disease (ESRD) facility'],
        ['8', 'Special facility or hospital'],
    ])
    return codeMap.get(code)
}

export const fromTypeOfCareCode = (code: string) => {
    const codeMap = new Map<string, string>([
        ['1', ' Inpatient Part A'],
        ['2', ' Inpatient Part B'],
        ['3', 'Outpatient'],
        ['4', 'Other (Part B)'],
        ['5', 'Intermediate Care - Level I'],
        ['6', 'Intermediate Care - Level II'],
        ['7', 'Subacute Inpatient'],
    ])
    return codeMap.get(code)
}

export const formatNumberWithThousandSeparator = (num: number) =>
    num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

export const calculateHash = (value: string): string => {
    return crypto.createHash('sha256').update(value).digest('hex')
}

export const fromHttpStatusCode = (code: string) => {
    if (!code) return 'UNKNOWN'
    const codeMap = new Map<string, string>([
        ['200', '200 (OK) Success'],
        ['201', 'Created'],
        ['202', 'Accepted'],
        ['203', 'Non-Authoritative Information'],
        ['204', 'No Content'],
        ['205', 'Reset Content'],
        ['206', 'Partial Content'],
        ['300', 'Multiple Choices'],
        ['301', 'Moved Permanently'],
        ['302', 'Found'],
        ['303', 'See Other'],
        ['304', 'Not Modified'],
        ['305', 'Use Proxy'],
        ['306', 'Unused'],
        ['307', 'Temporary Redirect'],
        ['400', 'Bad Request'],
        ['401', 'Unauthorized'],
        ['402', 'Payment Required'],
        ['403', 'Forbidden'],
        ['404', 'Not Found'],
        ['405', 'Method Not Allowed'],
        ['406', 'Not Acceptable'],
        ['407', 'Proxy Authentication Required'],
        ['408', 'Request Timeout'],
        ['409', 'Conflict'],
        ['410', 'Gone'],
        ['411', 'Length Required'],
        ['412', 'Precondition Required'],
        ['413', 'Request Entry Too Large'],
        ['414', 'Request-URI Too Long'],
        ['415', 'Unsupported Media Type'],
        ['416', 'Requested Range Not Satisfiable'],
        ['417', 'Expectation Failed'],
        ['418', "I'm a teapot"],
        ['429', 'Too Many Requests'],
        ['500', '500 (ERROR)'],
        ['501', 'Not Implemented'],
        ['502', 'Bad Gateway'],
        ['503', 'Service Unavailable'],
        ['504', 'Gateway Timeout'],
        ['505', 'HTTP Version Not Supported'],
    ])
    return codeMap.get(code)
}
